import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ArrowUpRight, Share2, Copy, Sparkles, Gift } from "lucide-react";
import { useLang } from "../LanguageContext";
import { useAuth } from "../AuthContext";
import { useSettings } from "../SettingsContext";
import { useCurrency } from "../CurrencyContext";
import BirthdayBanner from "../components/BirthdayBanner";
import EditableText from "../components/EditableText";
import EditableI18n from "../components/EditableI18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIMES = [
  "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
  "17:00", "17:30", "18:00", "18:30", "19:00",
];

export default function Booking() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { settings } = useSettings();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    service_id: "",
    appointment_date: "",
    appointment_time: "",
    notes: "",
    referral_code: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [refValidation, setRefValidation] = useState(null); // {valid, owner_name, discount_fcfa} | null
  const [refChecking, setRefChecking] = useState(false);
  const [availability, setAvailability] = useState(null); // {date, capacity, booked, full}
  const [wlJoined, setWlJoined] = useState(false);
  const [wlSubmitting, setWlSubmitting] = useState(false);
  const [wlError, setWlError] = useState("");

  // Check daily availability when a date is picked
  useEffect(() => {
    setWlJoined(false);
    setWlError("");
    if (!form.appointment_date) { setAvailability(null); return; }
    axios.get(`${API}/bookings/availability`, { params: { date_str: form.appointment_date } })
      .then((r) => setAvailability(r.data))
      .catch(() => setAvailability(null));
  }, [form.appointment_date]);

  const joinWaitlist = async () => {
    setWlError("");
    if (!form.full_name || !form.phone) {
      setWlError(lang === "fr" ? "Renseignez votre nom et téléphone ci-dessous." : "Please fill in your name and phone below.");
      return;
    }
    setWlSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email || null,
        service_id: form.service_id || null,
        date: form.appointment_date,
        language: lang,
      });
      setWlJoined(true);
    } catch (err) {
      if (err.response?.status === 409) {
        setWlError(lang === "fr" ? "Vous êtes déjà sur la liste d'attente pour ce jour." : "You're already on the wait-list for this day.");
      } else {
        setWlError(lang === "fr" ? "Une erreur est survenue. Réessayez." : "Something went wrong. Please try again.");
      }
    } finally {
      setWlSubmitting(false);
    }
  };

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, full_name: user.name, email: user.email }));
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => {
      const arr = r.data.services || [];
      setServices(arr);
      const params = new URLSearchParams(location.search);
      const preSel = params.get("service");
      if (preSel && arr.find((s) => s.id === preSel)) {
        setForm((f) => ({ ...f, service_id: preSel }));
      }
      const refParam = params.get("ref");
      if (refParam) {
        setForm((f) => ({ ...f, referral_code: refParam.toUpperCase() }));
      }
    }).catch(() => {});
  }, [location.search]);

  // Validate referral code when typed (debounced)
  useEffect(() => {
    const code = (form.referral_code || "").trim().toUpperCase();
    if (!code) { setRefValidation(null); return; }
    if (code.length < 4) { setRefValidation(null); return; }
    setRefChecking(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/referral/check/${encodeURIComponent(code)}`);
        setRefValidation(res.data);
      } catch {
        setRefValidation({ valid: false });
      } finally {
        setRefChecking(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.referral_code]);

  const today = new Date().toISOString().split("T")[0];
  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, { ...form, language: lang });
      setSuccess(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.detail === "day_full") {
        setAvailability((a) => ({ ...(a || {}), date: form.appointment_date, full: true }));
        setError(lang === "fr" ? "Cette journée est désormais complète — rejoignez la liste d'attente ci-dessous." : "This day just filled up — join the wait-list below.");
      } else {
        setError(t.booking.errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSuccess(null);
    setForm({
      full_name: user?.name || "", email: user?.email || "", phone: "",
      service_id: "", appointment_date: "", appointment_time: "", notes: "", referral_code: "",
    });
    setRefValidation(null);
  };

  // Referral
  const referralUrl = "https://queenswaystudio.com";
  const referralText = lang === "fr"
    ? `Je viens de réserver chez Queensway Studio à Dakar — soins beauté ultra-luxueux. Réserve avec moi et on a chacune 5 000 FCFA sur notre prochain IV Drip ! ${referralUrl}`
    : `I just booked at Queensway Studio in Dakar — ultra-luxury beauty treatments. Book with me and we both get 5,000 FCFA off our next IV Drip! ${referralUrl}`;
  const waShare = `https://wa.me/?text=${encodeURIComponent(referralText)}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <main data-testid="booking-page" className="pt-32 min-h-screen">
      <section className="px-6 md:px-12 lg:px-20 py-12 md:py-20">
        <p className="qw-overline mb-6"><EditableI18n k="booking.overline" label="Booking overline" /></p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-foreground text-balance mb-8 max-w-3xl">
          <EditableI18n k="booking.title" label="Booking title" />
        </h1>
        <p className="text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed"><EditableI18n k="booking.subtitle" label="Booking subtitle" /></p>
      </section>

      <div className="qw-divider mx-6 md:mx-12 lg:mx-20" />

      {/* Birthday banner — shown above form when user has active birthday code */}
      {!success && user?.birthday?.is_birthday_month && user?.birthday?.code && (
        <section className="px-6 md:px-12 lg:px-20 pt-10">
          <BirthdayBanner compact />
        </section>
      )}

      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-20">
        {success ? (
          <div data-testid="booking-success" className="max-w-3xl mx-auto py-8">
            <div className="text-center mb-10">
              <div className="h-20 w-20 mx-auto mb-8 flex items-center justify-center border border-primary">
                <Check size={32} strokeWidth={1.2} className="text-primary" />
              </div>
              <p className="qw-overline mb-4">{t.booking.successTitle}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">{t.booking.successTitle}</h2>
              <p className="text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">
                {t.booking.successBody.replace("10,000", settings.deposit_fcfa?.toLocaleString("en-US") || "10,000").replace("10 000", (settings.deposit_fcfa || 10000).toLocaleString("fr-FR"))}
              </p>
            </div>

            {/* Aftercare */}
            {success.aftercare && (success.aftercare.en || success.aftercare.fr) && (
              <div data-testid="aftercare-block" className="border border-primary/40 p-8 mb-10">
                <p className="qw-overline mb-3 flex items-center gap-2"><Sparkles size={12} strokeWidth={1.2} />{t.aftercare.title}</p>
                <p className="text-sm text-foreground/85 leading-relaxed">
                  {lang === "fr" ? success.aftercare.fr : success.aftercare.en}
                </p>
              </div>
            )}

            {/* Referral */}
            <div data-testid="referral-block" className="border border-border p-8 mb-10 bg-muted/30">
              <p className="qw-overline mb-3">{t.referral.title}</p>
              <p className="text-sm text-foreground/85 mb-6 leading-relaxed">{t.referral.body}</p>
              <div className="flex flex-wrap gap-3">
                <a href={waShare} target="_blank" rel="noreferrer" data-testid="referral-whatsapp-btn"
                   className="qw-btn qw-btn-primary !text-[0.7rem] !py-2.5 !px-4">
                  <Share2 size={12} strokeWidth={1.2} /> {t.referral.whatsapp}
                </a>
                <button data-testid="referral-copy-btn" onClick={copyLink} className="qw-btn qw-btn-outline !text-[0.7rem] !py-2.5 !px-4">
                  <Copy size={12} strokeWidth={1.2} /> {copied ? t.referral.copied : t.referral.copy}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-5">
              <button onClick={reset} data-testid="booking-new-btn" className="qw-btn qw-btn-outline">
                {t.booking.newRequest}
              </button>
              <button onClick={() => navigate(user ? "/account" : "/")} data-testid="booking-home-btn" className="qw-btn qw-btn-primary">
                {user ? t.auth.account : t.nav.home} <ArrowUpRight size={14} strokeWidth={1.2} />
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5">
              <p className="qw-overline mb-4"><EditableText kEn="txt_booking_note_label_en" kFr="txt_booking_note_label_fr" label="Note label" fallback={{ en: "Note", fr: "Note" }} /></p>
              <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-6 leading-snug">
                <EditableText kEn="txt_booking_note_title_en" kFr="txt_booking_note_title_fr" label="Booking note title"
                  fallback={{ en: "A ritual for every face.", fr: "Un rituel pour chaque visage." }} />
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-10">
                {t.deposit.note.replace("10,000", settings.deposit_fcfa?.toLocaleString("en-US") || "10,000").replace("10 000", (settings.deposit_fcfa || 10000).toLocaleString("fr-FR"))}
              </p>
              {!user && (
                <div className="border-l-2 border-primary/60 pl-5 py-2 mb-8">
                  <p className="text-xs text-muted-foreground mb-2">
                    <EditableText kEn="txt_booking_tip_en" kFr="txt_booking_tip_fr" label="Account tip"
                      fallback={{
                        en: "Tip — create an account to track appointments and unlock loyalty.",
                        fr: "Astuce — créez un compte pour suivre vos rendez-vous et débloquer la fidélité.",
                      }} />
                  </p>
                  <a href="/signup" className="qw-link-underline text-xs uppercase tracking-wider text-primary">
                    {t.auth.signup}
                  </a>
                </div>
              )}
            </div>

            <div className="lg:col-span-7 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="qw-overline block mb-2"><EditableI18n k="booking.fullName" label="Name label" /></label>
                  <input data-testid="booking-name-input" type="text" required value={form.full_name}
                    onChange={(e) => handleChange("full_name", e.target.value)} className="qw-input" />
                </div>
                <div>
                  <label className="qw-overline block mb-2"><EditableI18n k="booking.phone" label="Phone label" /></label>
                  <input data-testid="booking-phone-input" type="tel" required value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)} className="qw-input" />
                </div>
              </div>
              <div>
                <label className="qw-overline block mb-2"><EditableI18n k="booking.email" label="Email label" /></label>
                <input data-testid="booking-email-input" type="email" required value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)} className="qw-input" />
              </div>
              <div>
                <label className="qw-overline block mb-2"><EditableI18n k="booking.service" label="Service label" /></label>
                <select data-testid="booking-service-select" required value={form.service_id}
                  onChange={(e) => handleChange("service_id", e.target.value)}
                  className="qw-input appearance-none cursor-pointer">
                  <option value="" disabled>{t.booking.selectService}</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id} className="bg-background text-foreground">
                      {(lang === "fr" ? s.name_fr : s.name_en) + (s.price_fcfa === 0 ? " — " + t.services.priceOnConsult : " — " + format(s.price_fcfa))}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="qw-overline block mb-2"><EditableI18n k="booking.date" label="Date label" /></label>
                  <input data-testid="booking-date-input" type="date" required min={today} value={form.appointment_date}
                    onChange={(e) => handleChange("appointment_date", e.target.value)} className="qw-input" />
                </div>
                <div>
                  <label className="qw-overline block mb-2"><EditableI18n k="booking.time" label="Time label" /></label>
                  <select data-testid="booking-time-select" required value={form.appointment_time}
                    onChange={(e) => handleChange("appointment_time", e.target.value)}
                    className="qw-input appearance-none cursor-pointer">
                    <option value="" disabled>—</option>
                    {TIMES.map((tm) => <option key={tm} value={tm} className="bg-background">{tm}</option>)}
                  </select>
                </div>
              </div>

              {/* Wait-list panel when the day is fully booked */}
              {availability?.full && (
                <div data-testid="waitlist-panel" className="border border-primary/50 bg-primary/5 p-6">
                  {wlJoined ? (
                    <div data-testid="waitlist-joined">
                      <p className="qw-overline mb-2 text-primary">
                        {lang === "fr" ? "Vous êtes sur la liste" : "You're on the list"}
                      </p>
                      <p className="text-sm text-foreground/85 leading-relaxed">
                        {lang === "fr"
                          ? `Nous vous contacterons dès qu'une place se libère le ${form.appointment_date}.`
                          : `We'll reach out as soon as a spot opens on ${form.appointment_date}.`}
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="qw-overline mb-2 text-primary">
                        <EditableText kEn="booking_waitlist_title_en" kFr="booking_waitlist_title_fr" label="Wait-list panel title"
                          fallback={{ en: "This day is fully booked", fr: "Journée complète" }} />
                      </p>
                      <p className="text-sm text-foreground/85 leading-relaxed mb-5">
                        <EditableText kEn="booking_waitlist_body_en" kFr="booking_waitlist_body_fr" label="Wait-list panel text"
                          fallback={{
                            en: "Join the wait-list and we'll notify you the moment a spot opens. Or pick another date.",
                            fr: "Rejoignez la liste d'attente — nous vous préviendrons dès qu'une place se libère. Ou choisissez une autre date.",
                          }} />
                      </p>
                      <button type="button" onClick={joinWaitlist} disabled={wlSubmitting}
                        data-testid="waitlist-join-btn"
                        className="qw-btn qw-btn-primary !py-2.5 !px-5 !text-[0.7rem] disabled:opacity-60">
                        {wlSubmitting
                          ? (lang === "fr" ? "Envoi…" : "Joining…")
                          : (
                            <EditableText kEn="booking_waitlist_btn_en" kFr="booking_waitlist_btn_fr" label="Wait-list button label"
                              fallback={{ en: "Join the wait-list", fr: "Rejoindre la liste d'attente" }} />
                          )}
                      </button>
                      {wlError && <p data-testid="waitlist-error" className="text-xs text-destructive mt-3">{wlError}</p>}
                    </>
                  )}
                </div>
              )}
              <div>
                <label className="qw-overline block mb-2"><EditableI18n k="booking.notes" label="Notes label" /></label>
                <textarea data-testid="booking-notes-input" rows="3" value={form.notes}
                  onChange={(e) => handleChange("notes", e.target.value)} className="qw-input resize-none" />
              </div>
              <div>
                <label className="qw-overline block mb-2 flex items-center gap-2">
                  <Gift size={11} strokeWidth={1.2} className="text-primary" />
                  <EditableText kEn="txt_booking_referral_label_en" kFr="txt_booking_referral_label_fr" label="Referral code label"
                    fallback={{ en: "Referral code (optional)", fr: "Code de parrainage (optionnel)" }} />
                </label>
                <input
                  data-testid="booking-referral-input"
                  type="text"
                  value={form.referral_code}
                  onChange={(e) => handleChange("referral_code", e.target.value.toUpperCase())}
                  placeholder="QWXXXXXX"
                  className="qw-input uppercase tracking-wider"
                  maxLength={12}
                />
                {refChecking && (
                  <p className="text-xs text-muted-foreground mt-2">{lang === "fr" ? "Vérification…" : "Checking…"}</p>
                )}
                {!refChecking && refValidation && refValidation.valid && (
                  <p data-testid="referral-valid" className="text-xs text-primary mt-2">
                    {lang === "fr"
                      ? `✓ Parrainée par ${refValidation.owner_name}. Vous gagnez 5 000 FCFA après votre soin.`
                      : `✓ Referred by ${refValidation.owner_name}. You'll earn 5,000 FCFA credit after your visit.`}
                  </p>
                )}
                {!refChecking && refValidation && !refValidation.valid && form.referral_code && (
                  <p data-testid="referral-invalid" className="text-xs text-destructive mt-2">
                    {lang === "fr" ? "Code invalide." : "Invalid code."}
                  </p>
                )}
              </div>
              {error && <p data-testid="booking-error" className="text-sm text-destructive">{error}</p>}
              <div className="pt-4">
                <button type="submit" disabled={submitting || availability?.full} data-testid="booking-submit-btn"
                  className="qw-btn qw-btn-primary w-full md:w-auto disabled:opacity-60">
                  {submitting ? t.booking.submitting : <EditableI18n k="booking.submit" label="Submit button" />}
                  {!submitting && <ArrowUpRight size={16} strokeWidth={1.2} />}
                </button>
              </div>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}
